import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { City, Event, Webcam } from '../../types';
import type { UserData } from '../../api/client';

interface MapState {
    selectedCity: City | null;
    events: Event[];
    webcams: Webcam[];
    isTrafficVisible: boolean;
    isDpsMarkersVisible: boolean;
    isWebcamsMarkersVisible: boolean;
    isCleanMarkersVisible: boolean;
    isDtpMarkersVisible: boolean;
    mapToken: string;
    loading: boolean;
    error: string | null;
    centrifugoConnectionToken: string | null;
    cityTag: string | null;
    isMapReady: boolean;
    userPremium: boolean;
    isFiltersLoaded: boolean;
    userData: UserData | null;
    realUserCache: UserData | null;
    ghostUserCache: UserData | null;
}

const initialState: MapState = {
    selectedCity: null,
    events: [],
    webcams: [],
    isTrafficVisible: true,
    isDpsMarkersVisible: true,
    isWebcamsMarkersVisible: true, // По умолчанию включено
    isCleanMarkersVisible: true, // По умолчанию включено
    isDtpMarkersVisible: true, // По умолчанию включено
    mapToken: '',
    loading: false,
    error: null,
    centrifugoConnectionToken: null,
    cityTag: null,
    isMapReady: false, // Изначально карта не готова
    userPremium: false,
    isFiltersLoaded: false, // Фильтры еще не загружены из storage
    userData: null,
    realUserCache: null,
    ghostUserCache: null,
};

const findEventByKey = (events: Event[], event: Event) =>
    events.find(e =>
        e.latitude === event.latitude &&
        e.longitude === event.longitude &&
        e.timestamp === event.timestamp &&
        e.message === event.message
    );

const mapSlice = createSlice({
    name: 'map',
    initialState,
    reducers: {
        setUserPremium: (state, action: PayloadAction<boolean>) => {
            state.userPremium = action.payload;
        },
        setSelectedCity: (state, action: PayloadAction<City | null>) => {
            state.selectedCity = action.payload;
            //state.isMapReady = false;
            if (action.payload) {
                localStorage.setItem('selectedCityId', action.payload.id.toString());
            } else {
                localStorage.removeItem('selectedCityId');
            }
        },
        setEvents: (state, action: PayloadAction<{ events: Event[], isMailingEnabled?: boolean }>) => {
            // Проверяем премиум статус - без премиума события не загружаются
            if (!state.userPremium) {
                state.events = [];
                return;
            }

            if (action.payload.isMailingEnabled === false) {
                state.events = [];
                return;
            }
            state.events = action.payload.events;
        },
        setWebcams: (state, action: PayloadAction<Webcam[]>) => {
            state.webcams = action.payload;
        },
        updateEvents: (state, action: PayloadAction<{ events: Event[], isMailingEnabled?: boolean }>) => {
            if (!state.userPremium) {
                return;
            }

            if (action.payload.isMailingEnabled === false) {
                console.log("Рассылка отключена - не обновляем события");
                return;
            }
            const newEvents = action.payload.events
            const currentEvents = state.events;


            const updatedEvents: Event[] = [...currentEvents];

            for (const newEvent of newEvents) {
                const existingEvent = findEventByKey(currentEvents, newEvent);

                if (!existingEvent) {

                    updatedEvents.unshift(newEvent);
                    console.log(`📍 Добавлено новое событие: ${newEvent.message?.slice(0, 30)}`);
                }
            }

            state.events = updatedEvents;

            console.log(`🔄 Обновление событий: было ${currentEvents.length}, стало ${updatedEvents.length}`);
        },

        toggleTraffic: (state) => {
            state.isTrafficVisible = !state.isTrafficVisible;
        },
        toggleDpsMarkers: (state) => {
            state.isDpsMarkersVisible = !state.isDpsMarkersVisible;
        },
        toggleWebcamsMarkers: (state) => {
            state.isWebcamsMarkersVisible = !state.isWebcamsMarkersVisible;
        },
        toggleCleanMarkers: (state) => {
            state.isCleanMarkersVisible = !state.isCleanMarkersVisible;
        },
        toggleDtpMarkers: (state) => {
            state.isDtpMarkersVisible = !state.isDtpMarkersVisible;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setCentrifugoConnectionToken: (state, action: PayloadAction<string>) => {
            state.centrifugoConnectionToken = action.payload;
        },
        setCityTag: (state, action: PayloadAction<string>) => {
            state.cityTag = action.payload;
        },
        setMapReady: (state, action: PayloadAction<boolean>) => {
            state.isMapReady = action.payload;
        },
        setMapToken: (state, action: PayloadAction<string>) => {
            state.mapToken = action.payload;
        },
        setMapFiltersFromStorage: (state, action: PayloadAction<{
            isTrafficVisible: boolean;
            isDpsMarkersVisible: boolean;
            isWebcamsMarkersVisible: boolean;
            isCleanMarkersVisible: boolean;
            isDtpMarkersVisible: boolean;
        }>) => {
            state.isTrafficVisible = action.payload.isTrafficVisible;
            state.isDpsMarkersVisible = action.payload.isDpsMarkersVisible;
            state.isWebcamsMarkersVisible = action.payload.isWebcamsMarkersVisible;
            state.isCleanMarkersVisible = action.payload.isCleanMarkersVisible;
            state.isDtpMarkersVisible = action.payload.isDtpMarkersVisible;
            state.isFiltersLoaded = true;
        },
        setFiltersLoaded: (state, action: PayloadAction<boolean>) => {
            state.isFiltersLoaded = action.payload;
        },
        setUserData: (state, action: PayloadAction<UserData | null>) => {
            state.userData = action.payload;
            if (action.payload) {
                if (action.payload.ghost_mode_enabled) {
                    state.ghostUserCache = action.payload;
                } else {
                    state.realUserCache = action.payload;
                }
            }
        },
    },
});

export const {
    setUserPremium,
    setSelectedCity,
    setEvents,
    setWebcams,
    updateEvents,
    toggleTraffic,
    toggleDpsMarkers,
    toggleWebcamsMarkers,
    toggleCleanMarkers,
    toggleDtpMarkers,
    setLoading,
    setError,
    setCentrifugoConnectionToken,
    setCityTag,
    setMapReady,
    setMapToken,
    setMapFiltersFromStorage,
    setFiltersLoaded,
    setUserData,
} = mapSlice.actions;

export const mapSlices = mapSlice.reducer;